import apiClient from './apiClient';
import {
  CREATE_MENU_ITEM,
  DELETE_MENU_ITEM,
  EDIT_MENU_ITEM,
  GET_MENU_ITEM_LOCATIONS,
  LINK_MENU_ITEM_RESTRICTIONS,
  REORDER_MENU_ITEMS,
  HIDE_SHOW_MENU_ITEM,
  LINK_MENU_ITEM_TAGS,
  PAIR_MENU_ITEM,
  LINK_MENU_ITEM_MODIFIER_GROUPS,
  LINK_MENU_ITEM_MEDIA
} from '../constants/UriConstants';
import {
  CreateMenuItemInterface,
  CreateMenuItemResponse,
  DetailedMenuItemInterface,
  EditMenuItemResponse,
  MenuItemCopyInterface
} from '../types/MenuItemInterface';
import { LocationScope } from '../types/MenuInterface';
import { buildLocationScopeBody, restaurantHeaders } from '../utils/locationScope';

export const createMenuItem = (
  menuItem: CreateMenuItemInterface,
  locationScope: LocationScope = 'single',
  restaurantIDs: number[] = []
): Promise<CreateMenuItemResponse> =>
  apiClient
    .post(CREATE_MENU_ITEM, { ...menuItem, ...buildLocationScopeBody(locationScope, restaurantIDs) })
    .then((response) => response.data);

// restaurantID targets a copy at another location; defaults to the current location
export const deleteMenuItem = async (menuItemID: number, restaurantID?: number) => {
  await apiClient.delete(DELETE_MENU_ITEM + menuItemID, restaurantHeaders(restaurantID));
};

// resolves with the edited copies for a multi-location edit, and with undefined for this location only
export const editMenuItem = async (
  menuItem: DetailedMenuItemInterface,
  menuSectionID: number,
  locationScope: LocationScope = 'single',
  restaurantIDs: number[] = []
): Promise<EditMenuItemResponse | undefined> => {
  const response = await apiClient.put(EDIT_MENU_ITEM, {
    ...menuItem,
    menuSectionID,
    ...buildLocationScopeBody(locationScope, restaurantIDs)
  });
  return locationScope === 'single' ? undefined : response.data;
};

// every location with a copy of this menu or drink item (just its own location when it has no copies)
export const getMenuItemLocations = (menuItemID: number): Promise<MenuItemCopyInterface[]> =>
  apiClient.get(GET_MENU_ITEM_LOCATIONS(menuItemID)).then((response) => response.data);

export const showMenuItem = async (menuItemID: number) => {
  await apiClient.put(HIDE_SHOW_MENU_ITEM, { menuItemID, hide: false });
};

export const hideMenuItem = async (menuItemID: number, reappearAt?: string) => {
  await apiClient.put(HIDE_SHOW_MENU_ITEM, { menuItemID, hide: true, reappearAt });
};

export const pairMenuItem = async (menuItemID: number, pairingItemIDs: number[]) => {
  await apiClient.put(PAIR_MENU_ITEM, { menuItemID, pairingItemIDs });
};

export const linkModifierGroupsToMenuItem = async (menuItemID: number, modifierGroupIDs: number[]) => {
  await apiClient.put(LINK_MENU_ITEM_MODIFIER_GROUPS, { menuItemID, modifierGroupIDs });
};

// restaurantID targets a copy at another location; defaults to the current location
export const linkRestrictionsToMenuItem = async (
  menuItemID: number,
  dietaryRestrictionIDs: number[],
  restaurantID?: number
) => {
  await apiClient.put(
    LINK_MENU_ITEM_RESTRICTIONS,
    { menuItemID, dietaryRestrictionIDs },
    restaurantHeaders(restaurantID)
  );
};

export const linkTagsToMenuItem = async (menuItemID: number, tagIDs: number[]) => {
  await apiClient.put(LINK_MENU_ITEM_TAGS, { menuItemID, tagIDs });
};

export const reorderMenuItems = async (menuSectionID: number, menuItemsOrder: number[]) => {
  await apiClient.patch(REORDER_MENU_ITEMS, { menuSectionID, menuItemsOrder });
};

// locations lists the item's copies at other locations; the backend copies the media into
// each location's Media Library (same stored file) and links it to that copy
export const linkMediaToMenuItem = async (
  menuItemID: number,
  mediaIDs: number[],
  thumbnails: { videoID: number; thumbnailID: number }[],
  locations?: MenuItemCopyInterface[]
) => {
  await apiClient.post(LINK_MENU_ITEM_MEDIA, {
    menuItemID,
    mediaIDs,
    thumbnails,
    ...(locations?.length && { locations })
  });
};
